import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { usePathname } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { useAuthContext } from 'src/auth/hooks';
import { useAppBootstrap } from 'src/contexts/app-bootstrap-context';
import { useOnboardingState } from 'src/hooks/use-onboarding';
import { validateSubscription } from 'src/api/billing';
import { getStepPath } from 'src/constants/onboarding';
import { getSelectedCompany } from 'src/utils/company-storage';
import { getStoredCompanySettings } from 'src/services/companySettings';
import { GET_SHOPIFY_PERMISSION_STATUS } from 'src/api/queries/getShopifyPermissionStatus';
import type { RouteRequirements, AppReadinessResult } from 'src/types/route-metadata';
import type { ShopifyPermissionResponse } from 'src/types/shopify-reapproval';

// ----------------------------------------------------------------------

interface UseAppReadinessProps {
  requirements?: RouteRequirements;
}

/**
 * Policy engine hook that determines app readiness based on route requirements.
 *
 * This consolidates the logic previously spread across multiple guards:
 * - AuthGuard: authentication check
 * - CompanyContextGuard: company selection check
 * - OnboardingRedirectGuard: onboarding completion check
 *
 * Returns a single decision: ready, loading, or redirect with destination.
 */
export function useAppReadiness({
  requirements = {},
}: UseAppReadinessProps = {}): AppReadinessResult {
  const pathname = usePathname();
  const { authenticated, loading: authLoading } = useAuthContext();
  const {
    selectedCompany,
    companies,
    status: bootstrapStatus,
    isLoadingCompanies,
    // New bootstrap data fields
    validationComplete,
    subscriptionValid: bootstrapSubscriptionValid,
    shopifyPermissionStatus: bootstrapShopifyPermissionStatus,
  } = useAppBootstrap();

  // Check if we have cached data for optimistic loading
  const hasCachedData = useMemo(() => {
    const cachedCompany = getSelectedCompany();
    const cachedSettings = getStoredCompanySettings();
    return !!cachedCompany && !!cachedSettings;
  }, []);

  // Always call the onboarding hook, but only use the values when we have a selected company
  // We can skip this if we have bootstrap data
  const { isNew, currentStep, loading: isLoadingOnboarding } = useOnboardingState();

  // useOnboardingState already handles bootstrap fallback + live query updates.
  const effectiveIsNew = isNew;
  const effectiveCurrentStep = currentStep;
  const effectiveOnboardingLoading = isLoadingOnboarding;

  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [subscriptionValid, setSubscriptionValid] = useState(true);

  // Use bootstrap subscription status if available
  const effectiveSubscriptionValid = bootstrapSubscriptionValid ?? subscriptionValid;
  // Only considered "checked" if:
  // 1. Bootstrap provided subscription status (bootstrapSubscriptionValid !== null), OR
  // 2. Fallback check has completed (subscriptionChecked is true)
  // NOT just because validationComplete is true - bootstrap might not have provided subscription data
  const effectiveSubscriptionChecked = bootstrapSubscriptionValid !== null || subscriptionChecked;

  // Query Shopify permission status when company is selected and required
  // Skip if bootstrap is providing the data OR if we're waiting for bootstrap
  const shouldSkipShopifyQuery =
    !requirements.needsShopifyPermissions ||
    !selectedCompany?.id ||
    !validationComplete || // Wait for bootstrap to complete first
    bootstrapShopifyPermissionStatus !== null; // Bootstrap already provided data

  const {
    data: shopifyPermissionData,
    loading: loadingShopifyPermissions,
    error: shopifyPermissionError,
  } = useQuery<ShopifyPermissionResponse>(GET_SHOPIFY_PERMISSION_STATUS, {
    skip: shouldSkipShopifyQuery,
    errorPolicy: 'all', // Continue even if there are errors
  });

  // Use bootstrap Shopify permission data if available
  const effectiveShopifyPermissionStatus =
    bootstrapShopifyPermissionStatus ?? shopifyPermissionData?.getShopifyPermissionStatus;
  // Only show loading if bootstrap hasn't completed yet (not while waiting for fallback query)
  const effectiveShopifyPermissionLoading =
    !validationComplete || (!shouldSkipShopifyQuery && loadingShopifyPermissions);

  // Check subscription when we have a company and it's required
  // Skip if we have bootstrap data
  useEffect(() => {
    const checkSubscription = async () => {
      if (
        !requirements.needsValidSubscription ||
        !selectedCompany?.id ||
        subscriptionChecked ||
        !validationComplete || // Wait for bootstrap to complete first
        bootstrapSubscriptionValid !== null // Bootstrap already provided subscription status
      ) {
        return;
      }
      // If we get here, bootstrap completed but didn't provide subscription status - make fallback call

      try {
        const subscriptionResult = await validateSubscription(selectedCompany.id);
        setSubscriptionValid(subscriptionResult.has_subscription);

        if (!subscriptionResult.has_subscription) {
          sessionStorage.setItem(
            'subscription_required_message',
            'Your subscription has expired or is inactive. Please update your billing to continue using the application.'
          );
        }
      } catch (error) {
        console.error('Error checking subscription status:', error);
        setSubscriptionValid(true);
      } finally {
        setSubscriptionChecked(true);
      }
    };

    checkSubscription();
  }, [
    selectedCompany?.id,
    subscriptionChecked,
    requirements.needsValidSubscription,
    validationComplete,
    bootstrapSubscriptionValid,
  ]);

  return useMemo(() => {
    // 1. Check authentication requirement
    if (requirements.needsAuth && !authenticated) {
      // While auth is still determining session, show loading instead of redirecting
      if (authLoading) {
        return {
          state: 'loading',
          reason: 'Authenticating',
        };
      }
      return {
        state: 'redirect',
        redirectTo: paths.auth.amplify.login,
        reason: 'Authentication required',
      };
    }

    // 2. If authenticated but company data still loading
    if (authenticated && requirements.needsCompany) {
      // Optimistic loading: if we have cached data and are validating in background, show ready
      const isEffectivelyLoading = hasCachedData
        ? false // Don't show loading if we have cached data
        : bootstrapStatus === 'loading' || isLoadingCompanies;

      if (isEffectivelyLoading) {
        return {
          state: 'loading',
          reason: 'Loading companies',
        };
      }

      // 3. Check bootstrap error (e.g. database issues loading companies)
      if (bootstrapStatus === 'error' && !hasCachedData) {
        return {
          state: 'redirect',
          redirectTo: paths.auth.amplify.companySelection,
          reason: 'Failed to load companies',
        };
      }

      // 4. Check company selection requirement
      if (companies.length > 1 && !selectedCompany) {
        return {
          state: 'redirect',
          redirectTo: paths.auth.amplify.companySelection,
          reason: 'Company selection required',
        };
      }

      // 5. Check if no companies available (error case)
      // Only treat as "no companies" after bootstrap finished at least one fetch cycle
      // AND we don't have cached data
      if (bootstrapStatus === 'ready' && companies.length === 0 && !hasCachedData) {
        return {
          state: 'redirect',
          redirectTo: paths.auth.amplify.companySelection,
          reason: 'No companies available',
        };
      }
    }

    // 6. Check onboarding completion requirement
    if (requirements.needsOnboardingComplete && selectedCompany) {
      // Still loading onboarding state (only if no bootstrap data yet)
      if (effectiveOnboardingLoading) {
        return {
          state: 'loading',
          reason: 'Loading onboarding state',
        };
      }

      // Check onboarding completion using effective values
      if (effectiveIsNew) {
        // Skip onboarding check if already on onboarding routes
        if (pathname.startsWith(paths.onboarding.root)) {
          return {
            state: 'ready',
            reason: 'On onboarding route',
          };
        }

        // Smart redirection: redirect to current step instead of always welcome
        const redirectPath = getStepPath(effectiveCurrentStep);

        return {
          state: 'redirect',
          redirectTo: redirectPath,
          reason: 'Onboarding required',
        };
      }
    }

    // 7. Check subscription requirement
    if (requirements.needsValidSubscription && selectedCompany) {
      if (!effectiveSubscriptionChecked) {
        return {
          state: 'loading',
          reason: 'Checking subscription status',
        };
      }

      if (!effectiveSubscriptionValid) {
        return {
          state: 'redirect',
          redirectTo: paths.settings.billing,
          reason: 'Valid subscription required',
        };
      }
    }

    // 8. Check Shopify permissions requirement
    if (requirements.needsShopifyPermissions && selectedCompany) {
      if (effectiveShopifyPermissionLoading) {
        return {
          state: 'loading',
          reason: 'Checking Shopify permissions',
        };
      }

      // Check for reapproval errors from middleware or GraphQL
      const isReapprovalError =
        shopifyPermissionError &&
        (shopifyPermissionError.message.includes('SHOPIFY_REAPPROVAL_REQUIRED') ||
          shopifyPermissionError.message.includes('Shopify permission reapproval required') ||
          (shopifyPermissionError as any)?.graphQLErrors?.some(
            (err: any) => err?.extensions?.code === 'SHOPIFY_REAPPROVAL_REQUIRED'
          ));

      // If we have permission data indicating reapproval needed, or any reapproval error
      if (effectiveShopifyPermissionStatus?.requiresReapproval || isReapprovalError) {
        return {
          state: 'redirect',
          redirectTo: paths.shopify.reapproval,
          reason: 'Shopify permission reapproval required',
        };
      }

      // If we have other GraphQL errors (not reapproval), continue anyway
      if (shopifyPermissionError && !isReapprovalError) {
        console.warn('Error checking Shopify permissions, continuing:', shopifyPermissionError);
      }
    }

    // All requirements met
    return {
      state: 'ready',
      reason: 'All requirements satisfied',
    };
  }, [
    requirements,
    authenticated,
    pathname,
    selectedCompany,
    companies,
    bootstrapStatus,
    isLoadingCompanies,
    hasCachedData,
    effectiveIsNew,
    effectiveCurrentStep,
    effectiveOnboardingLoading,
    effectiveSubscriptionChecked,
    effectiveSubscriptionValid,
    effectiveShopifyPermissionStatus,
    effectiveShopifyPermissionLoading,
    authLoading,
    shopifyPermissionError,
  ]);
}

/**
 * Convenience hook for routes that need all standard protections
 */
export function useStandardAppReadiness(): AppReadinessResult {
  return useAppReadiness({
    requirements: {
      needsAuth: true,
      needsCompany: true,
      needsOnboardingComplete: true,
      needsValidSubscription: true,
      needsShopifyPermissions: true,
    },
  });
}

/**
 * Convenience hook for splash-driven root redirect.
 *
 * Root redirect must resolve the full protected-route policy so login can flow:
 * login -> splash -> final destination
 */
export function useRootRedirectReadiness(): AppReadinessResult {
  return useAppReadiness({
    requirements: {
      needsAuth: true,
      needsCompany: true,
      needsValidSubscription: true,
      needsOnboardingComplete: true,
      needsShopifyPermissions: true,
    },
  });
}
